/**
 * ConsChips — чипы норм расходников квеста/спецверсии (A33): «{название}
 * {норма} {ед}/игрок|/игра ✕»; норма — NumPad int (max 99, юнит из
 * справочника), тап по /игрок|/игра — toggle режима. «+ расходник» — Dropdown
 * multi (meta = единица, уже добавленные disabled) → цепочка NumPad по
 * onNext (эталон consChips js:383-397). Единица приходит ТОЛЬКО из
 * справочника — в квесте её не выбирают. ResChips — ресурсы (bool-привязка
 * без количества, эталон js:246-255).
 *
 * screen-18 (форма услуги): openQtyPop — qty-чип открывает NumPadPop хоста
 * у самого чипа (эталон-18 js:250-251) вместо NumPad-шита; squareAdd —
 * квадрат «+» .opq.add вместо текстовой selBtn (js:226, 259). Без пропов
 * поведение квестов (A33) прежнее.
 */
import { useRef, useState } from "react";
import { Dropdown, NumPad, type NumPadOpts } from "../../components/shared";
import type { Consumable, Resource } from "../../api/types";
import type { ConsItem } from "../../hooks/useQuestsAdmin";
import { QuickDictCreate } from "./QuickDictCreate";
import { CrossIco } from "./questIcons";
import css from "./quests.module.css";

/** Конфиг NumPadPop у qty-чипа (эталон-18 js:250-251). */
export interface QtyPopCfg {
  anchor: HTMLElement; label: string; mode: "int"; unit: string; max: number;
  value: number; onCommit: (v: number) => void;
}

/** Инлайн-размер квадрата «+» (эталон js:226/259: width auto, padding 0 5px). */
const SQ_ADD = { width: "auto", padding: "0 5px" } as const;

export interface ConsChipsProps {
  items: ConsItem[];
  cat: Consumable[];
  onChange: (next: ConsItem[]) => void;
  /** Добавление из цепочки NumPad — по одному (функциональный апдейт хоста). */
  onAdd: (consumableId: string, qty: number) => void;
  /** screen-18: qty-чип зовёт NumPadPop хоста; без пропа — NumPad-шит. */
  openQtyPop?: (cfg: QtyPopCfg) => void;
  /** screen-18: квадрат «+» .opq.add вместо текстовой selBtn. */
  squareAdd?: boolean;
}

export function ConsChips({ items, cat, onChange, onAdd, openQtyPop, squareAdd }: ConsChipsProps) {
  const [npIdx, setNpIdx] = useState<number | null>(null);
  const [chain, setChain] = useState<NumPadOpts | null>(null);
  const [dd, setDd] = useState<HTMLElement | null>(null);
  /* 31.08 (владелец): «+ новый расходник» прямо из дропдауна — созданные в
     этой сессии живут в локальном хвосте каталога, пока refetch не привезёт */
  const [made, setMade] = useState<Consumable[]>([]);
  const [createAt, setCreateAt] = useState<HTMLElement | null>(null);
  /* onAdd — через ref (stRef-паттерн Dropdown): opts цепочки фиксируются
     NumPad'ом при открытии, а onAdd хоста капчерит serverCons своего рендера —
     замыкание из state затирало бы приехавшие с сервера нормы. */
  const onAddRef = useRef(onAdd);
  onAddRef.current = onAdd;

  const fullCat = made.length ? cat.concat(made.filter((m) => !cat.some((x) => x.id === m.id))) : cat;
  const nameOf = (id: string) => fullCat.find((x) => x.id === id)?.name ?? id;
  const unitOf = (id: string) => fullCat.find((x) => x.id === id)?.unit || "шт";
  const cur = npIdx !== null ? items[npIdx] : undefined;

  const startChain = (ids: string[]) => {
    const o = (i: number): NumPadOpts => ({
      label: `${nameOf(ids[i]!)} — норма (${unitOf(ids[i]!)})`,
      mode: "int", unit: unitOf(ids[i]!), max: 99, value: 1,
      onCommit: (v) => onAddRef.current(ids[i]!, v || 1),
      onNext: i < ids.length - 1 ? () => o(i + 1) : undefined,
    });
    setChain(o(0));
  };

  return (
    <div className={css.chips}>
      {items.map((it, i) => (
        <span key={it.consumable_id} className={css.chip}>
          <span className={css.ct}>{nameOf(it.consumable_id)}</span>{" "}
          <button
            type="button" className={`${css.cq} ${css.num}`} title="Норма"
            onClick={(e) => (openQtyPop
              ? openQtyPop({
                  anchor: e.currentTarget, label: `${nameOf(it.consumable_id)} — норма`,
                  mode: "int", unit: unitOf(it.consumable_id), max: 99, value: it.qty,
                  onCommit: (v) => onChange(items.map((x, j) => (j === i ? { ...x, qty: v || 1 } : x))),
                })
              : setNpIdx(i))}
          >
            {it.qty}
          </button>{" "}
          <span className={css.cu}>{unitOf(it.consumable_id)}</span>
          <button
            type="button"
            className={`${css.cper} ${it.per_player ? css.pl : css.ga}`}
            title="Переключить режим"
            onClick={() => onChange(items.map((x, j) => (j === i ? { ...x, per_player: x.per_player ? 0 : 1 } : x)))}
          >
            /{it.per_player ? "игрок" : "игра"}
          </button>
          <button type="button" className={css.cx} onClick={() => onChange(items.filter((_, j) => j !== i))}>
            <CrossIco />
          </button>
        </span>
      ))}
      {squareAdd ? (
        <button
          type="button" className={`${css.opq} ${css.oAdd}`} style={SQ_ADD}
          title="Добавить расходник" onClick={(e) => setDd(e.currentTarget)}
        >
          +
        </button>
      ) : (
        <button type="button" className={css.selBtn} onClick={(e) => setDd(e.currentTarget)}>
          + расходник
        </button>
      )}
      <Dropdown
        open={!!dd}
        anchor={dd}
        title="Расходники"
        action={{ label: "+ новый расходник…", onPress: () => setCreateAt(dd) }}
        items={fullCat.map((x) => ({
          id: x.id, label: x.name, meta: x.unit || "шт",
          disabled: items.some((c) => c.consumable_id === x.id),
        }))}
        onCommit={(ids) => {
          const fresh = ids.map(String).filter((id) => !items.some((c) => c.consumable_id === id));
          if (fresh.length) startChain(fresh);
        }}
        onClose={() => setDd(null)}
      />
      <NumPad
        open={npIdx !== null}
        label={cur ? `${nameOf(cur.consumable_id)} — норма (${unitOf(cur.consumable_id)})` : ""}
        mode="int"
        unit={cur ? unitOf(cur.consumable_id) : undefined}
        max={99}
        value={cur?.qty}
        onCommit={(v) => {
          if (npIdx === null) return;
          onChange(items.map((x, j) => (j === npIdx ? { ...x, qty: v || 1 } : x)));
        }}
        onClose={() => setNpIdx(null)}
      />
      <NumPad open={chain !== null} {...(chain ?? {})} onClose={() => setChain(null)} />
      {createAt ? (
        <QuickDictCreate kind="c" anchor={createAt} onClose={() => setCreateAt(null)}
          onCreated={(row) => {
            setMade((m) => [...m, { id: row.id, name: row.name, unit: row.unit, active: 1 }]);
            /* норма сразу; opts строим из row — nameOf этого рендера ещё не видит made */
            setChain({
              label: `${row.name} — норма (${row.unit || "шт"})`,
              mode: "int", unit: row.unit || "шт", max: 99, value: 1,
              onCommit: (v) => onAddRef.current(row.id, v || 1),
            });
          }} />
      ) : null}
    </div>
  );
}

export interface ResChipsProps {
  ids: string[];
  cat: Resource[];
  onChange: (next: string[]) => void;
  /** screen-18: квадрат «+» .opq.add вместо текстовой selBtn. */
  squareAdd?: boolean;
}

/** Чипы ресурсов: имя + ✕; «+ ресурс» — Dropdown multi из справочника. */
export function ResChips({ ids, cat, onChange, squareAdd }: ResChipsProps) {
  const [dd, setDd] = useState<HTMLElement | null>(null);
  const [made, setMade] = useState<Resource[]>([]);
  const [createAt, setCreateAt] = useState<HTMLElement | null>(null);
  const fullCat = made.length ? cat.concat(made.filter((m) => !cat.some((x) => x.id === m.id))) : cat;
  const nameOf = (id: string) => fullCat.find((x) => x.id === id)?.name ?? id;
  return (
    <div className={css.chips}>
      {ids.map((id) => (
        <span key={id} className={css.chip}>
          {nameOf(id)}
          <button type="button" className={css.cx} onClick={() => onChange(ids.filter((x) => x !== id))}>
            <CrossIco />
          </button>
        </span>
      ))}
      {squareAdd ? (
        <button
          type="button" className={`${css.opq} ${css.oAdd}`} style={SQ_ADD}
          title="Добавить ресурс" onClick={(e) => setDd(e.currentTarget)}
        >
          +
        </button>
      ) : (
        <button type="button" className={css.selBtn} onClick={(e) => setDd(e.currentTarget)}>
          + ресурс
        </button>
      )}
      <Dropdown
        open={!!dd}
        anchor={dd}
        title="Ресурсы"
        action={{ label: "+ новый ресурс…", onPress: () => setCreateAt(dd) }}
        items={fullCat.map((x) => ({ id: x.id, label: x.name, disabled: ids.includes(x.id) }))}
        onCommit={(sel) => {
          const fresh = sel.map(String).filter((id) => !ids.includes(id));
          if (fresh.length) onChange([...ids, ...fresh]);
        }}
        onClose={() => setDd(null)}
      />
      {createAt ? (
        <QuickDictCreate kind="r" anchor={createAt} onClose={() => setCreateAt(null)}
          onCreated={(row) => {
            setMade((m) => [...m, { id: row.id, name: row.name, active: 1 }]);
            onChange([...ids, row.id]); /* созданный сразу привязан */
          }} />
      ) : null}
    </div>
  );
}
